import { ReportStatusEnum } from 'src/slices/services/api'
import { isPreFinalizeCsvExportOrg } from './org'

export type ReportStatusTitle =
  | '報告待ち'
  | '報告中断'
  | '報告済み'
  | '再送済み'
  | '確定済み'
  | '連携済み'

export type BadgeColor =
  | 'red'
  | 'green'
  | 'green04'
  | 'lightGreen'
  | 'yellow'
  | 'lightYellow'
  | 'gray'
  | 'vermilion'

export type FillTypeColor = 'color' | 'white' | 'transparent'

export type ReportStatusMap = {
  [key in ReportStatusEnum]: {
    badgeColor: BadgeColor
    fillTypeColor: FillTypeColor
    title: ReportStatusTitle
    isBeforeReported: boolean
  }
}

export const reportStatusMap: ReportStatusMap = {
  [ReportStatusEnum.Waiting]: {
    badgeColor: 'vermilion',
    fillTypeColor: 'color',
    title: '報告待ち',
    isBeforeReported: true,
  },
  [ReportStatusEnum.LeftHalfway]: {
    badgeColor: 'vermilion',
    fillTypeColor: 'color',
    title: '報告中断',
    isBeforeReported: true,
  },
  [ReportStatusEnum.Reported]: {
    badgeColor: 'green04',
    fillTypeColor: 'color',
    title: '報告済み',
    isBeforeReported: false,
  },
  [ReportStatusEnum.ImageResent]: {
    badgeColor: 'lightGreen',
    fillTypeColor: 'color',
    title: '再送済み',
    isBeforeReported: false,
  },
  [ReportStatusEnum.FinalizedReportSubmitted]: {
    badgeColor: 'lightYellow',
    fillTypeColor: 'color',
    title: '確定済み',
    isBeforeReported: false,
  },
  [ReportStatusEnum.FinalizedReportSynced]: {
    badgeColor: 'gray',
    fillTypeColor: 'color',
    title: '連携済み',
    isBeforeReported: false,
  },
}

export const getReportStatusTitle = (role?: ReportStatusEnum): string => {
  return role ? (reportStatusMap[role].title ?? '') : ''
}

export const getReportStatusBadgeColor = (
  status?: ReportStatusEnum
): BadgeColor => {
  return status ? (reportStatusMap[status].badgeColor ?? 'gray') : 'gray'
}

export const getReportStatusFillTypeColor = (
  status?: ReportStatusEnum
): FillTypeColor => {
  return status
    ? (reportStatusMap[status].fillTypeColor ?? 'transparent')
    : 'transparent'
}

export const isReportStatusBeforeReported = (
  reportStatus: ReportStatusEnum
): boolean => {
  return reportStatus ? reportStatusMap[reportStatus].isBeforeReported : false
}

// CSV出力対象のステータス。org別ポリシーの単一の情報源とし、
// ボタンの活性判定と出力対象の絞り込みの両方が isCsvExportable 経由で参照する (Issue #3408)
const CSV_EXPORTABLE_STATUSES_DEFAULT = [
  ReportStatusEnum.FinalizedReportSubmitted,
]
// 確定前出力org（パルコ）は報告済み・再送済みのみ。確定済みは出力できない。
// NOTE: この2値は domain/parcoCsv.ts のCSVステータス文言と対になっている。
//       出力対象を増減させたらラベル側も型エラーになるよう、型を共有して同期を強制する。
const CSV_EXPORTABLE_STATUSES_PRE_FINALIZE = [
  ReportStatusEnum.Reported,
  ReportStatusEnum.ImageResent,
]

export type PreFinalizeCsvExportableStatus =
  (typeof CSV_EXPORTABLE_STATUSES_PRE_FINALIZE)[number]

const csvExportableStatuses = (orgCode: string): ReportStatusEnum[] =>
  isPreFinalizeCsvExportOrg(orgCode)
    ? CSV_EXPORTABLE_STATUSES_PRE_FINALIZE
    : CSV_EXPORTABLE_STATUSES_DEFAULT

export const isCsvExportable = (
  orgCode: string,
  reportStatus: ReportStatusEnum
): boolean => csvExportableStatuses(orgCode).includes(reportStatus)

const NON_FINALIZABLE_STATUSES: ReportStatusEnum[] = [
  ReportStatusEnum.FinalizedReportSynced,
]

// 精査画面で確定値を登録できるステータスかどうかを判定する
export const isFinalizable = (reportStatus: ReportStatusEnum): boolean =>
  !NON_FINALIZABLE_STATUSES.includes(reportStatus)

export const statusCheckOptions: {
  label: string
  value: ReportStatusEnum
}[] = [
  {
    label: getReportStatusTitle(ReportStatusEnum.Waiting),
    value: ReportStatusEnum.Waiting,
  },
  {
    label: getReportStatusTitle(ReportStatusEnum.LeftHalfway),
    value: ReportStatusEnum.LeftHalfway,
  },
  {
    label: getReportStatusTitle(ReportStatusEnum.Reported),
    value: ReportStatusEnum.Reported,
  },
  {
    label: getReportStatusTitle(ReportStatusEnum.ImageResent),
    value: ReportStatusEnum.ImageResent,
  },
  {
    label: getReportStatusTitle(ReportStatusEnum.FinalizedReportSubmitted),
    value: ReportStatusEnum.FinalizedReportSubmitted,
  },
  {
    label: getReportStatusTitle(ReportStatusEnum.FinalizedReportSynced),
    value: ReportStatusEnum.FinalizedReportSynced,
  },
]
